import { getQuoteStatus } from './../utils/quoteUtils';
import { JobClient, JobStatus } from './common';
import { Job, JobQuote, ListingType } from './job';
import { City } from './profile';
import {
  BudgetRange,
  QuoteOwner,
  QuoteTask,
  Quote,
  ChildQuote,
  QuoteJob,
  QuoteAlliance,
} from './quote';
import { ServiceCategory } from './service';
import { FreelancerSearchResult } from './search';
import { AllianceStatus } from './alliance';

export interface QuoteSnapshotTask {
  id: string;
  name: string;
  description: string | null;
  pricing_type: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  sort_order: number;
}

export interface QuoteSnapshotChildQuote {
  quote_id: string;
  freelancer_id: string;
  total_amount: number;
  tasks: QuoteSnapshotTask[];
}

export interface QuoteSnapshot {
  quote_id: string;
  total_amount: number;
  tasks: QuoteSnapshotTask[];
  child_quotes: QuoteSnapshotChildQuote[];
}

export interface HubInteraction {
  id: string;
  notification_type: string;

  // 🔹 Destination
  destination_profile_id: string;
  destination_first_name: string | null;
  destination_last_name: string | null;
  destination_avatar_url: string | null;
  destination_role: string | null;

  // 🔹 Source
  source_profile_id: string | null;
  source_first_name: string | null;
  source_last_name: string | null;
  source_avatar_url: string | null;
  source_role: string | null;

  // 🔹 Source stats
  source_overall_rating: number;
  source_reviews_count: number;
  source_projects_count: number;
  source_alliance_count: number;

  // 🔹 Destination stats
  destination_overall_rating: number;
  destination_reviews_count: number;
  destination_projects_count: number;
  destination_alliance_count: number;

  // 🔹 Source city
  source_city_id: number | null;
  source_city_name: string | null;
  source_city_code: string | null;
  source_city_latitude: number | null;
  source_city_longitude: number | null;

  // 🔹 Destination city
  destination_city_id: number | null;
  destination_city_name: string | null;
  destination_city_code: string | null;
  destination_city_latitude: number | null;
  destination_city_longitude: number | null;

  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export interface HubAttachment {
  path: string;
  file_name: string;
  size: number;
}

export interface QuoteAllianceMeta {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'retired';
  reject_reason: string | null;
  request_message: string | null;
  service_category: ServiceCategory;
}

export interface AllianceQuote {
  id: string;
  status: ReturnType<typeof getQuoteStatus>;
  message: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;

  freelancer: {
    id: string;
    first_name: string;
    last_name: string | null;
    avatar_url: string | null;
    services?: ServiceCategory[];
    stats?: {
      alliance_count: number;
      overall_rating: number;
      projects_count: number;
      reviews_count: number;
    };
  };

  tasks: QuoteTask[];

  alliance_meta: QuoteAllianceMeta;
}

export interface PrincipalQuote {
  id: string;
  status: ReturnType<typeof getQuoteStatus>;
  message: string | null;
  change_request_message?: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;

  quote_owner: {
    id: string;
    first_name: string;
    last_name: string | null;
    avatar_url: string | null;
    services?: ServiceCategory[];
    stats?: {
      alliance_count: number;
      overall_rating: number;
      projects_count: number;
      reviews_count: number;
    };
  };

  tasks: QuoteTask[];
  alliances: AllianceQuote[];
}

/** Shape of a quote_request row as returned by get_hub_context (includes joined freelancer). */
export interface HubQuoteRequest {
  id: string;
  job_id: string;
  target_freelancer_id: string;
  client_id: string;
  status: string;
  reject_reason: string | null;
  created_at: string;
  updated_at: string;
  freelancer: {
    id: string;
    first_name: string;
    avatar_url: string | null;
  };
}

export interface HubContext {
  job: {
    id: string;
    title: string;
    description: string;
    on_site: boolean;
    languages: string[];
    start_date: string | null;
    end_date: string | null;
    status: JobStatus;
    visibility: 'public' | 'private';
    published_at: string | null;
    expires_at: string | null;
    selection_date: string | null;
    completed_at: string | null;
    created_at: string;
    updated_at: string;
    city: City;
    client: JobClient;
    budget_range: BudgetRange;
    listing_type: ListingType | null;
    required_services: ServiceCategory[];
    quote_requests: HubQuoteRequest[];
    has_user_quote: boolean;
    quotes: PrincipalQuote[];
  };

  quotes: Array<{
    id: string;
    status: string;
    submitted_at: string | null;
    change_request_message?: string | null;
    freelancer_id: string;
    quote_owner_first_name: string;
    quote_owner_last_name: string | null;
    quote_owner_avatar_url: string | null;
  }>;

  accepted_quote: {
    id: string;
    status: string;
    freelancer_id: string;
    quote_owner_first_name: string;
    quote_owner_last_name: string | null;
    quote_owner_avatar_url: string | null;
  } | null;

  view_type: 'recruiter_multi' | 'recruiter_project' | 'freelancer' | 'alliance';

  /** When view_type is freelancer or alliance: id of the principal quote to fetch via fetch_quote_by_id. */
  principal_quote_id?: string | null;
}

export type SubmissionRow = {
  id: string;
  type: 'principal' | 'alliance';
  status: string;
  submitted_at: string | null;

  freelancer: {
    id: string;
    first_name: string;
    last_name: string | null;
    avatar_url: string | null;
  };

  totalAmount: number;
  teamAvatars?: string[];
  teamMembers?: AllianceQuote[];
  stats?: {
    alliance_count: number;
    overall_rating: number;
    projects_count: number;
    reviews_count: number;
  };
};

export type Submissions = SubmissionRow[];

export function mapQuotesToSubmissions(quotes: PrincipalQuote[]): Submissions {
  return quotes.flatMap(quote => {
    const principalTotal =
      quote.tasks?.reduce((sum, task) => sum + (task.total_amount || 0), 0) ?? 0;

    const principal: SubmissionRow = {
      id: quote.id,
      type: 'principal',
      status: quote.status,
      submitted_at: quote.submitted_at,
      freelancer: quote.quote_owner,
      totalAmount: principalTotal,
      teamAvatars: quote.alliances?.map(a => a.freelancer.avatar_url || ''),
      teamMembers: quote.alliances,
      stats: quote.quote_owner.stats,
    };

    const alliances: SubmissionRow[] =
      quote.alliances?.map(alliance => ({
        id: alliance.id,
        type: 'alliance',
        status: alliance.status,
        submitted_at: alliance.submitted_at,
        freelancer: alliance.freelancer,
        totalAmount: alliance.tasks?.reduce((sum, task) => sum + (task.total_amount || 0), 0) ?? 0,
        teamAvatars: quote.alliances?.map(a => a.freelancer.avatar_url || ''),
        teamMembers: quote.alliances,
      })) || [];

    return [principal, ...alliances];
  });
}

export function mapQuotesToSubmissionsForAlliances(quotes: PrincipalQuote[]): Submissions {
  return quotes.flatMap(quote => {
    const alliances: SubmissionRow[] =
      quote.alliances?.map(alliance => ({
        id: alliance.id,
        type: 'alliance',
        status: alliance.status,
        submitted_at: alliance.submitted_at,
        freelancer: alliance.freelancer,
        totalAmount: alliance.tasks?.reduce((sum, task) => sum + (task.total_amount || 0), 0) ?? 0,
        teamAvatars: quote.alliances?.map(a => a.freelancer.avatar_url || ''),
        teamMembers: quote.alliances,
      })) || [];

    return alliances;
  });
}

export function mapQuotesToSubmissionsForPrincipals(quotes: PrincipalQuote[]): Submissions {
  return quotes.flatMap(quote => {
    const principalTotal =
      quote.tasks?.reduce((sum, task) => sum + (task.total_amount || 0), 0) ?? 0;

    const principal: SubmissionRow = {
      id: quote.id,
      type: 'principal',
      status: quote.status,
      submitted_at: quote.submitted_at,
      freelancer: quote.quote_owner,
      totalAmount: principalTotal,
      teamAvatars: quote.alliances?.map(a => a.freelancer.avatar_url || ''),
      teamMembers: quote.alliances,
    };

    return [principal];
  });
}

export function transformPrincipalQuoteToJobQuote(principalQuotes: PrincipalQuote[]): JobQuote[] {
  return principalQuotes.map(principalQuote => ({
    id: principalQuote.id,
    status: principalQuote.status,
    message: principalQuote.message,
    created_at: principalQuote.created_at,
    updated_at: principalQuote.updated_at,
    submitted_at: principalQuote.submitted_at,
    tasks: principalQuote.tasks,
    change_request_message: principalQuote.change_request_message,
    alliances: principalQuote.alliances.map(allianceQuote => ({
      id: allianceQuote.id,
      invited_freelancer: {
        profile_id: allianceQuote.freelancer.id,
        first_name: allianceQuote.freelancer.first_name,
        last_name: allianceQuote.freelancer.last_name || '',
        avatar_url: allianceQuote.freelancer.avatar_url,
        services: allianceQuote.freelancer.services,
        stats: allianceQuote.freelancer.stats,
      },
      service_category: allianceQuote.alliance_meta.service_category,
      status: allianceQuote.alliance_meta.status,
      created_at: allianceQuote.created_at,
      updated_at: allianceQuote.updated_at,
      child_quote:
        allianceQuote.tasks.length > 0
          ? {
              id: allianceQuote.id,
              status: allianceQuote.alliance_meta.status,
              submitted_at: allianceQuote.submitted_at,
              total_amount: allianceQuote.tasks.reduce((sum, task) => sum + task.total_amount, 0),
              tasks: allianceQuote.tasks,
              quote_owner: {
                id: allianceQuote.freelancer.id,
                first_name: allianceQuote.freelancer.first_name,
                last_name: allianceQuote.freelancer.last_name || '',
                avatar_url: allianceQuote.freelancer.avatar_url,
                services: allianceQuote.freelancer.services,
                stats: allianceQuote.freelancer.stats,
              } as QuoteOwner,
            }
          : null,
    })),
    quote_owner: {
      id: principalQuote.quote_owner.id,
      first_name: principalQuote.quote_owner.first_name,
      last_name: principalQuote.quote_owner.last_name || '',
      avatar_url: principalQuote.quote_owner.avatar_url,
      services: principalQuote.quote_owner.services,
      stats: principalQuote.quote_owner.stats,
    },
  }));
}

export function transformHubContextJobToJob(hubContextJob: HubContext['job']): Job {
  return {
    id: hubContextJob.id,
    title: hubContextJob.title,
    description: hubContextJob.description,
    on_site: hubContextJob.on_site,
    start_date: hubContextJob.start_date,
    end_date: hubContextJob.end_date,
    published_at: hubContextJob.published_at,
    expires_at: hubContextJob.expires_at,
    selection_date: hubContextJob.selection_date,
    completed_at: hubContextJob.completed_at,
    status: hubContextJob.status,
    languages: hubContextJob.languages,
    visibility: hubContextJob.visibility,
    city: hubContextJob.city,
    client: hubContextJob.client,
    budget_range: hubContextJob.budget_range,
    required_services: hubContextJob.required_services,
    listing_type: hubContextJob.listing_type || { id: 0, code: '' },
    created_at: hubContextJob.created_at,
    updated_at: hubContextJob.updated_at,
    quotes: hubContextJob.quotes.map(principalQuote => ({
      id: principalQuote.id,
      status: principalQuote.status,
      message: principalQuote.message,
      change_request_message: principalQuote.change_request_message ?? null,
      created_at: principalQuote.created_at,
      updated_at: principalQuote.updated_at,
      submitted_at: principalQuote.submitted_at,
      tasks: principalQuote.tasks,
      quote_owner: {
        id: principalQuote.quote_owner.id,
        first_name: principalQuote.quote_owner.first_name,
        last_name: principalQuote.quote_owner.last_name,
        avatar_url: principalQuote.quote_owner.avatar_url,
        services: principalQuote.quote_owner.services,
        stats: principalQuote.quote_owner.stats,
      } as QuoteOwner,
      alliances: principalQuote.alliances.map(allianceQuote => ({
        id: allianceQuote.id,
        invited_freelancer: {
          profile_id: allianceQuote.freelancer.id,
          first_name: allianceQuote.freelancer.first_name,
          last_name: allianceQuote.freelancer.last_name || '',
          avatar_url: allianceQuote.freelancer.avatar_url,
        },
        service_category: allianceQuote.alliance_meta.service_category,
        status: allianceQuote.alliance_meta.status,
        created_at: allianceQuote.created_at,
        updated_at: allianceQuote.updated_at,
        child_quote:
          allianceQuote.tasks.length > 0
            ? {
                id: allianceQuote.id,
                status: allianceQuote.alliance_meta.status,
                submitted_at: allianceQuote.submitted_at,
                total_amount: allianceQuote.tasks.reduce((sum, task) => sum + task.total_amount, 0),
                tasks: allianceQuote.tasks,
                quote_owner: {
                  id: allianceQuote.freelancer.id,
                  first_name: allianceQuote.freelancer.first_name,
                  last_name: allianceQuote.freelancer.last_name || '',
                  avatar_url: allianceQuote.freelancer.avatar_url,
                } as QuoteOwner,
              }
            : null,
      })),
    })),
    has_user_quote: hubContextJob.has_user_quote,
  };
}

export function transformHubContextToAcceptedQuote(hubContext: HubContext): Quote {
  const hubContextJob = hubContext.job;
  const principalQuote = hubContextJob.quotes.find(quote => quote.status === 'accepted');

  if (!principalQuote) {
    return null as unknown as Quote;
  }

  const job: QuoteJob = {
    id: hubContextJob.id,
    title: hubContextJob.title,
    description: hubContextJob.description,
    published_at: hubContextJob.published_at,
    status: hubContextJob.status,
    start_date: hubContextJob.start_date,
    end_date: hubContextJob.end_date,
    completed_at: hubContextJob.completed_at,
    visibility: hubContextJob.visibility,
    city: hubContextJob.city,
    budget_range: hubContextJob.budget_range,
    on_site: hubContextJob.on_site,
    languages: hubContextJob.languages,
    required_services: hubContextJob.required_services,
    client: hubContextJob.client,
  };

  const quote_owner: QuoteOwner = {
    id: principalQuote.quote_owner.id,
    first_name: principalQuote.quote_owner.first_name,
    last_name: principalQuote.quote_owner.last_name || '',
    avatar_url: principalQuote.quote_owner.avatar_url,
    services: principalQuote.quote_owner.services,
  };

  const alliances: QuoteAlliance[] = principalQuote.alliances.map(allianceQuote => ({
    id: allianceQuote.id,
    invited_freelancer: {
      profile_id: allianceQuote.freelancer.id,
      first_name: allianceQuote.freelancer.first_name,
      last_name: allianceQuote.freelancer.last_name || '',
      avatar_url: allianceQuote.freelancer.avatar_url,
    },
    service_category: allianceQuote.alliance_meta.service_category,
    status: allianceQuote.alliance_meta.status,
    created_at: allianceQuote.created_at,
    updated_at: allianceQuote.updated_at,
    child_quote:
      allianceQuote.tasks.length > 0
        ? {
            id: allianceQuote.id,
            status: allianceQuote.alliance_meta.status,
            submitted_at: allianceQuote.submitted_at,
            total_amount: allianceQuote.tasks.reduce((sum, task) => sum + task.total_amount, 0),
            tasks: allianceQuote.tasks,
            quote_owner: {
              id: allianceQuote.freelancer.id,
              first_name: allianceQuote.freelancer.first_name,
              last_name: allianceQuote.freelancer.last_name || '',
              avatar_url: allianceQuote.freelancer.avatar_url,
              services: allianceQuote.freelancer.services,
            } as QuoteOwner,
          }
        : null,
  }));

  return {
    id: principalQuote.id,
    job_id: hubContextJob.id,
    parent_quote_id: null,
    parent_quote_status: null,
    status: principalQuote.status,
    message: principalQuote.message,
    change_request_message: principalQuote.change_request_message ?? null,
    created_at: principalQuote.created_at,
    updated_at: principalQuote.updated_at,
    submitted_at: principalQuote.submitted_at,
    tasks: principalQuote.tasks,
    alliances,
    job,
    quote_alliance_status: null,
    parent_quote_owner: null,
    quote_owner,
  };
}

export function transformHubContextToQuote(
  hubContext: HubContext,
  principalQuote: PrincipalQuote
): Quote {
  const hubContextJob = hubContext.job;

  if (!principalQuote) {
    return null as unknown as Quote;
  }

  const job: QuoteJob = {
    id: hubContextJob.id,
    title: hubContextJob.title,
    description: hubContextJob.description,
    published_at: hubContextJob.published_at,
    status: hubContextJob.status,
    start_date: hubContextJob.start_date,
    end_date: hubContextJob.end_date,
    completed_at: hubContextJob.completed_at,
    visibility: hubContextJob.visibility,
    city: hubContextJob.city,
    budget_range: hubContextJob.budget_range,
    on_site: hubContextJob.on_site,
    languages: hubContextJob.languages,
    required_services: hubContextJob.required_services,
    client: hubContextJob.client,
  };

  const quote_owner: QuoteOwner = {
    id: principalQuote.quote_owner.id,
    first_name: principalQuote.quote_owner.first_name,
    last_name: principalQuote.quote_owner.last_name || '',
    avatar_url: principalQuote.quote_owner.avatar_url,
    services: principalQuote.quote_owner.services,
  };

  const alliances: QuoteAlliance[] = principalQuote.alliances.map(allianceQuote => ({
    id: allianceQuote.id,
    invited_freelancer: {
      profile_id: allianceQuote.freelancer.id,
      first_name: allianceQuote.freelancer.first_name,
      last_name: allianceQuote.freelancer.last_name || '',
      avatar_url: allianceQuote.freelancer.avatar_url,
    },
    service_category: allianceQuote.alliance_meta.service_category,
    status: allianceQuote.alliance_meta.status,
    created_at: allianceQuote.created_at,
    updated_at: allianceQuote.updated_at,
    child_quote:
      allianceQuote.tasks.length > 0
        ? {
            id: allianceQuote.id,
            status: allianceQuote.alliance_meta.status,
            submitted_at: allianceQuote.submitted_at,
            total_amount: allianceQuote.tasks.reduce((sum, task) => sum + task.total_amount, 0),
            tasks: allianceQuote.tasks,
            quote_owner: {
              id: allianceQuote.freelancer.id,
              first_name: allianceQuote.freelancer.first_name,
              last_name: allianceQuote.freelancer.last_name || '',
              avatar_url: allianceQuote.freelancer.avatar_url,
              services: allianceQuote.freelancer.services,
            } as QuoteOwner,
          }
        : null,
  }));

  return {
    id: principalQuote.id,
    job_id: hubContextJob.id,
    parent_quote_id: null,
    parent_quote_status: null,
    status: principalQuote.status,
    message: principalQuote.message,
    change_request_message: principalQuote.change_request_message ?? null,
    created_at: principalQuote.created_at,
    updated_at: principalQuote.updated_at,
    submitted_at: principalQuote.submitted_at,
    tasks: principalQuote.tasks,
    alliances,
    job,
    quote_alliance_status: null,
    parent_quote_owner: null,
    quote_owner,
  };
}

/** Resolve a quote for print from recruiter hub data (same source as BidderDetailsModal / job.quotes). */
export function findQuoteAndJobForPrintFromHub(
  hubContext: HubContext,
  quoteId: string
): { quote: Quote | ChildQuote; job: Job } | null {
  const job = transformHubContextJobToJob(hubContext.job);
  const want = String(quoteId);

  for (const principalQuote of hubContext.job.quotes) {
    if (String(principalQuote.id) === want) {
      return {
        quote: transformHubContextToQuote(hubContext, principalQuote),
        job,
      };
    }
    for (const allianceQuote of principalQuote.alliances) {
      if (String(allianceQuote.id) === want && allianceQuote.tasks.length > 0) {
        const childQuote: ChildQuote = {
          id: allianceQuote.id,
          status: allianceQuote.alliance_meta.status,
          submitted_at: allianceQuote.submitted_at,
          total_amount: allianceQuote.tasks.reduce((sum, task) => sum + task.total_amount, 0),
          tasks: allianceQuote.tasks,
          quote_owner: {
            id: allianceQuote.freelancer.id,
            first_name: allianceQuote.freelancer.first_name,
            last_name: allianceQuote.freelancer.last_name || '',
            avatar_url: allianceQuote.freelancer.avatar_url,
            services: allianceQuote.freelancer.services,
          },
        };
        return { quote: childQuote, job };
      }
    }
  }
  return null;
}

export function transformHubInteractionToFreelancerSearchResult(
  hubInteraction: HubInteraction,
  overrides?: {
    job_title?: string | null;
    service_category_slugs?: string[];
    bio?: string | null;
    recent_alliances?: Array<{
      profile_id: string;
      first_name: string;
      avatar_url: string | null;
    }> | null;
    full_access?: boolean;
    alliance_status?: AllianceStatus;
    distance?: number | null;
  }
): FreelancerSearchResult {
  return {
    id: hubInteraction.destination_profile_id || hubInteraction.id,
    first_name: hubInteraction.destination_first_name || '',
    last_name: hubInteraction.destination_last_name || null,
    job_title: overrides?.job_title || null,
    service_category_slugs: overrides?.service_category_slugs || [],
    avatar_url: hubInteraction.destination_avatar_url || undefined,
    bio: overrides?.bio || null,
    city: hubInteraction.destination_city_name || undefined,
    alliance_count: hubInteraction.destination_alliance_count || 0,
    recent_alliances: overrides?.recent_alliances || null,
    full_access: overrides?.full_access ?? false,
    alliance_status: overrides?.alliance_status || 'accepted',
    overall_rating: hubInteraction.destination_overall_rating || 0,
    reviews_count: hubInteraction.destination_reviews_count || 0,
    projects_count: hubInteraction.destination_projects_count || 0,
    distance: overrides?.distance || null,
  };
}
